import type { InputSchema } from "./inputValidation";

const id: InputSchema = {
  type: "string",
  minLength: 1,
  maxLength: 128,
  description:
    "Case-sensitive canvas ID. Use a returned ID for existing elements; choose a fresh unique ID for additions.",
};
const nullableId: InputSchema = { anyOf: [id, { type: "null" }] };
const number: InputSchema = { type: "number", minimum: -1000000, maximum: 1000000 };
const dimension: InputSchema = { type: "number", minimum: 0, maximum: 1000000 };
const color: InputSchema = {
  type: "string",
  minLength: 1,
  maxLength: 64,
  description:
    "CSS color: hex (for example #1971c2), named color (including transparent), rgb/rgba or hsl/hsla. URLs and expressions are rejected.",
};
const point: InputSchema = { type: "array", items: number, minItems: 2, maxItems: 2 };
const binding: InputSchema = {
  description:
    "Arrow endpoint binding; omit to preserve on update, null to detach. The target can be added in this batch. Endpoint geometry still comes from points.",
  anyOf: [
    { type: "null" },
    {
      type: "object",
      additionalProperties: false,
      properties: {
        elementId: {
          ...id,
          description:
            "Distinct live rectangle, ellipse, diamond, text, image or embeddable ID; not a frame, line or arrow.",
        },
        focus: {
          type: "number",
          minimum: -1,
          maximum: 1,
          description:
            "Native normalized attachment focus: 0 is centered; -1..1 offsets attachment across the target. Use 0 for a centered connection.",
        },
        gap: {
          ...dimension,
          description:
            "Nonnegative distance from the target boundary in scene units; for example 8.",
        },
        fixedPoint: {
          anyOf: [point, { type: "null" }],
          description:
            "Optional native target-relative [x,y] attachment coordinates (normally 0..1); null leaves the attachment non-fixed. Omit for ordinary arrows.",
        },
      },
      required: ["elementId", "focus", "gap"],
    },
  ],
};
const arrowhead: InputSchema = {
  description: "Arrow-only endpoint marker; null means no marker. Omit on update to preserve it.",
  anyOf: [
    { type: "null" },
    {
      type: "string",
      enum: [
        "arrow",
        "bar",
        "dot",
        "triangle",
        "triangle_outline",
        "circle",
        "circle_outline",
        "diamond",
        "diamond_outline",
        "crowfoot_one",
        "crowfoot_many",
        "crowfoot_one_or_many",
      ],
    },
  ],
};

const common = {
  x: {
    ...number,
    description:
      "Absolute horizontal scene coordinate, increasing right. Unrotated box origin; for lines/arrows, the origin of relative points. Not viewport pixels.",
  },
  y: {
    ...number,
    description:
      "Absolute vertical scene coordinate, increasing down. Unrotated box origin; for lines/arrows, the origin of relative points. Not viewport pixels.",
  },
  width: {
    ...dimension,
    description:
      "Unrotated width in scene units. Text is remeasured and linear geometry normalized; use text/fontSize or points for those types. Supply explicit dimensions for predictable new shapes.",
  },
  height: {
    ...dimension,
    description:
      "Unrotated height in scene units. Text is remeasured and linear geometry normalized; supply explicit dimensions for predictable new shapes.",
  },
  angle: {
    ...number,
    description:
      "Clockwise rotation in radians around the element center, not degrees. New elements normally start at 0.",
  },
  strokeColor: {
    ...color,
    description: `Outline/line color; for text, glyph color. ${color.description}`,
  },
  backgroundColor: {
    ...color,
    description: `Interior fill color; use transparent for no fill, fillStyle solid for a flat fill. ${color.description}`,
  },
  fillStyle: {
    type: "string",
    enum: ["solid", "hachure", "cross-hatch", "zigzag"],
    description:
      "Interior fill pattern; pair with backgroundColor. Omission uses native defaults on add and preserves on update.",
  },
  strokeWidth: {
    type: "number",
    minimum: 0,
    maximum: 100,
    description: "Stroke thickness in scene units.",
  },
  strokeStyle: {
    type: "string",
    enum: ["solid", "dashed", "dotted"],
    description: "Outline/line dash pattern.",
  },
  roughness: {
    type: "number",
    minimum: 0,
    maximum: 3,
    description: "Hand-drawn roughness: 0 is precise, larger values are sketchier.",
  },
  opacity: {
    type: "number",
    minimum: 0,
    maximum: 100,
    description: "Percent opacity: 0 invisible, 100 opaque (not a 0..1 fraction).",
  },
  locked: {
    type: "boolean",
    description:
      "True locks the element. Tools cannot modify/delete/unlock already-locked elements or dependencies; unlocking requires the editor UI.",
  },
  groupIds: {
    type: "array",
    items: id,
    maxItems: 20,
    description:
      "Replace ordered membership with unique group IDs; [] removes all groups. Group IDs are not element IDs. Membership does not move members; update each member explicitly.",
  },
  frameId: {
    ...nullableId,
    description:
      "Containing frame/magicframe ID, possibly added in this batch; null detaches. Omit to preserve on update. Frames cannot themselves belong to a frame; membership does not move geometry.",
  },
} satisfies Record<string, InputSchema>;

const text = {
  text: {
    type: "string",
    maxLength: 16000,
    description:
      "Text content, including newline characters. Required for new text; may be empty. Edit the separate text ID for a bound label, not its container. Dimensions are remeasured.",
  },
  textStyle: {
    type: "object",
    additionalProperties: false,
    description:
      "Whole-text semantic styling. Omitted flags preserve existing values; false clears a flag. No rich-text spans or raw customData.",
    properties: {
      bold: { type: "boolean", description: "True enables bold; false clears it." },
      italic: { type: "boolean", description: "True enables italic; false clears it." },
    },
  },
  fontSize: {
    type: "number",
    minimum: 1,
    maximum: 1000,
    description: "Font size in scene units; native text layout recalculates dimensions.",
  },
  fontFamily: {
    type: "integer",
    enum: [1, 2, 3, 5, 6, 7, 8, 9, 10],
    description:
      "Native font ID: 5 Excalifont (hand-drawn), 6 Nunito (sans), 7 Lilita One (display), 8 Comic Shanns (code); legacy 1 Virgil, 2 Helvetica, 3 Cascadia, 9 Liberation Sans. Preserve a returned 10 for native compatibility. Omit to use native add defaults/preserve on update.",
  },
  textAlign: {
    type: "string",
    enum: ["left", "center", "right"],
    description: "Horizontal text alignment within its text/container layout.",
  },
  verticalAlign: {
    type: "string",
    enum: ["top", "middle", "bottom"],
    description: "Vertical alignment, primarily for a bound label inside a shape.",
  },
  containerId: {
    ...nullableId,
    description:
      "Bind this text as the single label of a rectangle, ellipse, diamond or arrow (including same-batch additions). null detaches. Back-references and text layout are derived; never send boundElements.",
  },
} satisfies Record<string, InputSchema>;
const linear = {
  points: {
    type: "array",
    items: point,
    minItems: 2,
    maxItems: 200,
    description:
      "Relative [dx,dy] coordinates in scene units from x/y. First point MUST be [0,0]; at least two points. Replace the entire point list on update. Example: [[0,0],[160,0]] draws rightward.",
  },
} satisfies Record<string, InputSchema>;
const arrow = {
  startBinding: binding,
  endBinding: binding,
  startArrowhead: arrowhead,
  endArrowhead: arrowhead,
};
const frame = {
  name: {
    anyOf: [{ type: "string", maxLength: 256 }, { type: "null" }],
    description:
      "Frame/magicframe caption; null removes the explicit name. Not a drawing filename or text label.",
  },
} satisfies Record<string, InputSchema>;
export const canvasProperties: Readonly<Record<string, InputSchema>> = {
  ...common,
  ...text,
  ...linear,
  ...arrow,
  ...frame,
};
const object = (
  properties: Readonly<Record<string, InputSchema>>,
  required?: readonly string[],
  description?: string,
): InputSchema => ({
  type: "object",
  additionalProperties: false,
  properties,
  required,
  description,
});
const elementVariants = [
  ["rectangle", {}, []],
  ["ellipse", {}, []],
  ["diamond", {}, []],
  ["text", text, ["text"]],
  ["line", linear, ["points"]],
  ["arrow", { ...linear, ...arrow }, ["points"]],
  ["frame", frame, []],
] as const;
const element: InputSchema = {
  ...object({
    ...canvasProperties,
    id,
    type: {
      type: "string",
      description: "Native element type; select one of the seven supported add branches.",
    },
  }),
  description:
    "One new element. Choose the matching type branch. Common omitted properties use native Excalidraw defaults; specify geometry/style explicitly when exact appearance matters.",
  anyOf: elementVariants.map(([type, properties, required]) =>
    object(
      {
        ...common,
        ...properties,
        id,
        type: { type: "string", enum: [type], description: `Create a ${type} element.` },
      },
      ["id", "type", "x", "y", ...required],
      `New ${type}; only this branch's properties are accepted.`,
    ),
  ),
};
const operation: InputSchema = {
  description:
    "One operation per target ID in a batch. Relations may reference another new element in the same batch, regardless of order.",
  anyOf: [
    object(
      {
        op: {
          type: "string",
          enum: ["add"],
          description:
            "Create a new element with a fresh ID, including IDs of deleted elements as unavailable.",
        },
        element,
      },
      ["op", "element"],
    ),
    object(
      {
        op: {
          type: "string",
          enum: ["update"],
          description:
            "Change only supplied fields of an existing live element; omitted fields and unrelated metadata are preserved.",
        },
        id,
        properties: {
          ...object(canvasProperties),
          minProperties: 1,
          description:
            "Nonempty partial update. Read target type first: text/font/container fields only on text; points only on line/arrow; bindings/arrowheads only on arrow; name only on frame/magicframe. ID and type cannot change. Existing image/freehand content can be moved/styled with common fields, not replaced.",
        },
      },
      ["op", "id", "properties"],
    ),
    object(
      {
        op: {
          type: "string",
          enum: ["delete"],
          description:
            "Delete an existing live element. Container deletion also deletes its bound label and detaches connected arrows. Frame deletion detaches children, not deletes them.",
        },
        id,
      },
      ["op", "id"],
    ),
  ],
};
export const drawingPatchSchema: InputSchema = {
  ...object(
    {
      readToken: {
        ...id,
        description:
          "Opaque token returned by drawing_read on this canvas. Never invent it or use a cloud/file version. Retained up to five minutes and eight observations.",
      },
      operationId: {
        ...id,
        description:
          "Fresh request ID. Retry identical arguments with the same ID to retrieve the prior result (five minutes/128 receipts); use a new ID for revised input.",
      },
      operations: {
        type: "array",
        items: operation,
        minItems: 1,
        maxItems: 200,
        description:
          "Atomic batch: any invalid operation rejects the whole batch. At most 200 affected elements including derived dependency changes. No full-scene replacement.",
      },
    },
    ["readToken", "operationId", "operations"],
  ),
  examples: [
    {
      readToken: "READ_TOKEN",
      operationId: "add-shapes-1",
      operations: [
        {
          op: "add",
          element: {
            id: "new-box",
            type: "rectangle",
            x: 100,
            y: 100,
            width: 160,
            height: 80,
            backgroundColor: "#d0ebff",
            fillStyle: "solid",
          },
        },
        {
          op: "add",
          element: { id: "new-circle", type: "ellipse", x: 380, y: 100, width: 100, height: 100 },
        },
        {
          op: "add",
          element: { id: "new-decision", type: "diamond", x: 580, y: 100, width: 140, height: 100 },
        },
      ],
    },
    {
      readToken: "READ_TOKEN",
      operationId: "add-label-1",
      operations: [
        {
          op: "add",
          element: {
            id: "new-labeled-box",
            type: "rectangle",
            x: 100,
            y: 300,
            width: 200,
            height: 100,
          },
        },
        {
          op: "add",
          element: {
            id: "new-bound-label",
            type: "text",
            x: 110,
            y: 310,
            text: "Start",
            containerId: "new-labeled-box",
            fontSize: 20,
            fontFamily: 6,
            textAlign: "center",
            verticalAlign: "middle",
          },
        },
        {
          op: "add",
          element: {
            id: "new-note",
            type: "text",
            x: 100,
            y: 440,
            text: "Next steps\nReview the plan",
            textStyle: { bold: true },
          },
        },
      ],
    },
    {
      readToken: "READ_TOKEN",
      operationId: "add-connectors-1",
      operations: [
        {
          op: "add",
          element: { id: "new-source", type: "rectangle", x: 100, y: 100, width: 100, height: 80 },
        },
        {
          op: "add",
          element: { id: "new-target", type: "ellipse", x: 400, y: 100, width: 100, height: 80 },
        },
        {
          op: "add",
          element: {
            id: "new-connector",
            type: "arrow",
            x: 208,
            y: 140,
            points: [
              [0, 0],
              [184, 0],
            ],
            startBinding: { elementId: "new-source", focus: 0, gap: 8 },
            endBinding: { elementId: "new-target", focus: 0, gap: 8 },
            startArrowhead: null,
            endArrowhead: "arrow",
          },
        },
        {
          op: "add",
          element: {
            id: "new-line",
            type: "line",
            x: 100,
            y: 250,
            points: [
              [0, 0],
              [400, 0],
            ],
            strokeStyle: "dashed",
          },
        },
      ],
    },
    {
      readToken: "READ_TOKEN",
      operationId: "add-frame-1",
      operations: [
        {
          op: "add",
          element: {
            id: "new-frame",
            type: "frame",
            x: 0,
            y: 0,
            width: 600,
            height: 400,
            name: "Phase one",
          },
        },
        {
          op: "add",
          element: {
            id: "new-member",
            type: "rectangle",
            x: 80,
            y: 80,
            width: 160,
            height: 80,
            frameId: "new-frame",
            groupIds: ["new-group"],
          },
        },
      ],
    },
    {
      readToken: "READ_TOKEN",
      operationId: "edit-1",
      operations: [
        {
          op: "update",
          id: "EXISTING_SHAPE_ID",
          properties: {
            x: 240,
            backgroundColor: "#b2f2bb",
            fillStyle: "solid",
            groupIds: [],
            frameId: null,
          },
        },
        {
          op: "update",
          id: "EXISTING_TEXT_ID",
          properties: { text: "Updated label", textStyle: { bold: false, italic: true } },
        },
      ],
    },
    {
      readToken: "READ_TOKEN",
      operationId: "delete-1",
      operations: [{ op: "delete", id: "EXISTING_SHAPE_ID" }],
    },
  ],
};

const mode: InputSchema = {
  type: "string",
  default: "all",
  enum: ["overview", "all", "ids", "query", "selection", "changes"],
  description:
    "all (default): all live records; overview: counts/context only; ids/query/selection: subsets; changes: whole-canvas delta since readToken.",
};
const pageSize: InputSchema = {
  type: "integer",
  default: 50,
  minimum: 1,
  maximum: 100,
  description:
    "Maximum records per page, default 50; byte limits may yield fewer. Applies to the initial read only.",
};
const readFields = {
  mode,
  pageSize,
  ids: {
    type: "array",
    items: id,
    minItems: 1,
    maxItems: 200,
    description:
      "Existing element IDs for mode ids; missing/deleted IDs produce no records. Not drawing IDs.",
  },
  query: {
    ...object({
      types: {
        type: "array",
        items: {
          type: "string",
          enum: [
            "rectangle",
            "ellipse",
            "diamond",
            "text",
            "line",
            "arrow",
            "frame",
            "magicframe",
            "freedraw",
            "image",
            "embeddable",
          ],
        },
        minItems: 1,
        maxItems: 12,
        description:
          "Match any listed native type. Some readable types cannot be created by drawing_patch.",
      },
      text: {
        type: "string",
        minLength: 1,
        maxLength: 256,
        description:
          "Case-insensitive substring in text or a shape's bound label; not a regular expression.",
      },
      strokeColor: {
        ...color,
        description:
          "Case-insensitive equality with stored color string; equivalent CSS spellings are not normalized.",
      },
      backgroundColor: {
        ...color,
        description:
          "Case-insensitive equality with stored fill-color string; read first if the exact color is unknown.",
      },
      groupId: { ...id, description: "Require membership in this group." },
      frameId: { ...id, description: "Require membership in this frame." },
      region: object(
        {
          x: { ...number, description: "Left edge of scene-space query region." },
          y: { ...number, description: "Top edge of scene-space query region." },
          width: { ...dimension, description: "Region width in scene units." },
          height: { ...dimension, description: "Region height in scene units." },
        },
        ["x", "y", "width", "height"],
        "Match elements whose native rotated bounds intersect this scene-space rectangle; not screen coordinates.",
      ),
    }),
    minProperties: 1,
    description:
      "Nonempty filters combined with AND; types entries combine with OR. Only for mode query.",
  },
  readToken: {
    ...id,
    description:
      "Earlier drawing_read token for mode changes. Returns changed/new live records and deletedIds across the whole canvas, plus a new baseline token.",
  },
  cursor: {
    ...id,
    description:
      "Opaque nextCursor from the preceding read page. Send ONLY {cursor}; no mode, filters or pageSize. Pages retain the original snapshot/token and expire with it.",
  },
} satisfies Record<string, InputSchema>;
export const drawingReadSchema: InputSchema = {
  ...object(readFields),
  anyOf: [
    object({ mode: { ...mode, enum: ["all", "overview", "selection"] }, pageSize }),
    ...(["ids", "query", "changes"] as const).map((value) => {
      const key = value === "changes" ? "readToken" : value;
      return object({ mode: { ...mode, enum: [value] }, pageSize, [key]: readFields[key] }, [
        "mode",
        key,
      ]);
    }),
    object({ cursor: readFields.cursor }, ["cursor"]),
  ],
  examples: [
    {},
    { mode: "overview" },
    { mode: "selection" },
    { mode: "ids", ids: ["EXISTING_SHAPE_ID", "EXISTING_TEXT_ID"] },
    { mode: "query", query: { types: ["ellipse"], backgroundColor: "#1971c2" }, pageSize: 20 },
    { mode: "query", query: { text: "Start", region: { x: 0, y: 0, width: 800, height: 600 } } },
    { mode: "changes", readToken: "READ_TOKEN" },
    { cursor: "NEXT_CURSOR" },
  ],
};
